import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChange, SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Subject, fromEvent, of, Observable } from 'rxjs';
import { catchError, finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { HomeUiService } from '../new-design/home-ui.service';
import { FooziAiConciergeService } from '../new-design/foozi-ai-concierge.service';
import { MicroActionStateService, MicroActionSessionState } from './micro-action-state.service';
import { ToasterService } from '../toaster.service';
import { I18n } from '../i18n.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { RoutineV2Service } from '../routine-v2/routine-v2.service';
import { KyperDataApiService } from '../services/kyper-data-api.service';
import { AppState } from '../app.service';
import { PracticeBotModalComponent } from '../new-design/practice-bot-modal/practice-bot-modal.component';
import { PracticeBotService, PracticeCompletedAttempt } from '../new-design/practice-bot.service';
import { IonTextarea, ModalController, Platform } from '@ionic/angular';
import { Keyboard } from '@awesome-cordova-plugins/keyboard/ngx';
import mixpanel from 'mixpanel-browser';

export interface CoachAction {
  title: string;
  description: string;
}

export interface ChatBubble {
  role: 'coach' | 'user' | 'system';
  text: string;
  action?: CoachAction;
  descriptionHtml?: SafeHtml;        // pre-sanitized HTML for action card
  pendingAction?: CoachAction;
  pendingDescHtml?: SafeHtml;        // pre-sanitized HTML for pending action card
  showUseThis?: boolean;
  isStreaming?: boolean;
  showChips?: boolean;
  splitLayout?: boolean;             // text and action shown in separate background wrappers
}

@Component({
  selector: 'app-micro-action-chatbot-v1',
  templateUrl: './micro-action-chatbot-v1.component.html',
  styleUrls: ['./micro-action-chatbot-v1.component.scss'],
})
export class MicroActionChatbotV1Component implements OnInit, OnDestroy, AfterViewInit {
  @Input() habit: any;
  @Input() inverted = false;
  @Input() progressItems: any[] = [];
  @Input() currentIndex = 0;
  @Input() screenId = '';
  @Input() isDisabled = false;
  /**
   * Show this action, do not let it be changed.
   *
   * For a host that can render the action but cannot write to it — History opening one from a cycle
   * the learner is no longer in, where the row supplies the wording but not the step and screen ids
   * a customisation has to be saved against. Separate from inputDisabled, which the recommit flow
   * turns back on by itself.
   */
  @Input() readOnly = false;
  /**
   * This screen's content came from a history row rather than from the path.
   *
   * It changes where the action's ORIGINAL wording is read from. A row carries the latest
   * customisation — it is the newest version by definition — so the screen content cannot be
   * trusted as the starting point, and the thread's own opening event is the record of it. When
   * the path supplied the content, that content IS the action as published and no event beats it:
   * a thread that began after an earlier customisation would otherwise report that customisation
   * as the original.
   */
  @Input() contentFromHistory = false;
  /**
   * The action's wording before any customisation, where the host can state it outright.
   *
   * The server records it with every customisation (base_title / base_description), so a history
   * row can say what the action started as rather than leaving it to be worked out. Preferred over
   * the thread's own opening event, which is an inference: it can be missing on a conversation from
   * before that event existed, and it records how the action read when THAT thread began, which is
   * not the same thing as how it was published.
   */
  @Input() originalAction: CoachAction | null = null;
  @Input() footerActive = false;
  @Output() backgroundImageChange = new EventEmitter<string | null>();
  @Output() actionUpdated = new EventEmitter<CoachAction>();
  @Output() commitClicked = new EventEmitter<() => void>();
  @Output() openToolClicked = new EventEmitter<void>();
  @Output() markCompletedClicked = new EventEmitter<() => void>();

  @ViewChild('chatScroll') private chatScrollEl: ElementRef;
  @ViewChild('stepTitle') stepTitle: ElementRef;
  @ViewChild('msgInput') private msgInput!: IonTextarea;
  chat: ChatBubble[] = [];
  userInput = '';
  showScrollToBottom = false;
  private readonly BOTTOM_THRESHOLD = 1;
  private chatMutationObserver?: MutationObserver;
  currentAction: CoachAction = { title: '', description: '' };
  /** Completed practice attempts for this micro-action, oldest first — one card each. */
  practiceAttempts: PracticeCompletedAttempt[] = [];
  /**
   * Attempts that were already on record when the action was last customised. Their card still
   * shows — the session happened and its report is still there — but it is labelled as feedback
   * on the earlier wording. Tracked by id rather than by a cutoff time: the cutoff would be a
   * client clock compared against server timestamps in another ISO format.
   */
  private supersededAttemptIds = new Set<string>();
  /** How many messages had been said when the attempt was last read — the card follows them. */
  private practiceCardAfterCount = 0;
  /** Sequence for practice-history reads, so an older answer cannot overwrite a newer one. */
  private practiceHistoryRequestId = 0;
  /**
   * One page of this action's attempts, the same twenty the practice bot pages by.
   *
   * The whole history used to come down for a screen that shows a single card. Offset 0 is the
   * newest page — the bot's "show earlier history" walks oldest-ward from it — and attemptsFrom
   * sorts within whatever it is given, so the newest attempt is still the one on the card.
   */
  private readonly PRACTICE_PAGE_SIZE = 20;
  /**
   * Pages to walk back through looking for a completed attempt before giving up.
   *
   * A page can be all abandoned, failed or still-analyzing attempts, none of which this card can
   * show — and unlike the unpaged read, stopping at the first page would hide a completed attempt
   * the action really has. Bounded, because an action practised and abandoned fifty times must not
   * turn one card into a walk back through the learner's whole history.
   */
  private readonly PRACTICE_FILL_CHASES = 3;
  private baseAction: CoachAction = { title: '', description: '' };
  isLoading = false;
  isSending = false;
  inputDisabled = false;
  innerTextHtml: any;
  private pendingPresetKey: string | null = null;

  get canSchedule(): boolean { return !!this.habit?.screen?.content?.canSchedule; }
  get hasToolLink(): boolean { return !!this.habit?.screen?.content?.toolLink; }
  get canCompleteNow(): boolean { return !!this.habit?.screen?.content?.canCompleteNow; }
  /** The action's own switch for voice practice: no flag, no Practice chip and no practice calls. */
  /**
   * Practice is enabled per micro-action, AND requires the coach itself to be on: the two are
   * independent switches, but Practice Bot runs through the coach for now, so a client with the
   * coach off cannot reach it. `enableFoozi` undefined means on, as everywhere else.
   */
  get canVoicePractice(): boolean {
    if (!this.habit?.screen?.content?.enableVoicePractice) { return false; }
    return this.appState.get('currentClient')?.enableFoozi !== false;
  }
  // True when the action is completed per the local habit data OR per the live todoActions list.
  // todoActions is authoritative after a to-do-list completion that hasn't propagated back to habit.
  get isActionCompleted(): boolean {
    return !!this.habit?.screen?.userResponse?.userActionCompletion || this.isCompletedInTodoList;
  }
  get introText(): string { return this.habit?.screen?.content?.introText || 'Your next micro action is ready.'; }
  isActionAccepted = true;
  isRecommitMode = false;
  // Chips are disabled only when isDisabled is set externally AND the action is not already
  // completed (completed actions are still clickable — they trigger the recommit prompt).
  get actionChipsDisabled(): boolean { return this.isDisabled && !this.isActionCompleted; }

  keyboardOffset = 0;
  keyboardShowing = false;
  keyboardDismissing = false;
  textareaFocused = false;
  isCordovaMobile = false;
  isMobile = false;
  // When keyboard is visible, keyboardOffset already positions the input bar above
  // the keyboard (which sits above the footer), so do NOT add the footer 104px on top.
  // When textarea is focused (keyboard about to show), snap to 0 so animation starts
  // from 0, not 104 — eliminates the gap between keyboard start and input bar.
  get totalBottomOffset(): number {
    if (this.keyboardShowing) return this.keyboardOffset;
    if (this.textareaFocused) return 0;
    return this.footerActive ? 104 : 0;
  }

  private sessionId: string | null = null;
  private sessionSynced = false;
  private initialEventFired = false;

  private initSyncPromise: Promise<void> | null = null;
  private sessionIdPromise: Promise<string | null> | null = null;
  /**
   * What POST /session?screen_content_id=X answered with when this thread was resolved.
   *
   * Get-or-create returns the action as the server last stored it, alongside the session id. Kept
   * here so the screen reads its latest wording from the request it has already made rather than
   * asking again for the same thing.
   */
  private resolvedAction: any = null;
  private destroyed$ = new Subject<void>();
  private readonly httpCancel$ = new Subject<void>();
  private activeStreamInterval: ReturnType<typeof setInterval> | null = null;

  constructor(
    private fooziService: FooziAiConciergeService,
    public homeUIService: HomeUiService,
    private cdRef: ChangeDetectorRef,
    private render: Renderer2,
    private _sanitizer: DomSanitizer,
    private microActionState: MicroActionStateService,
    private keyboard: Keyboard,
    private platform: Platform,
    private toasterService: ToasterService,
    private i18n: I18n,
    private routineV2Service: RoutineV2Service,
    private kyperApiService: KyperDataApiService,
    private appState: AppState,
    private elRef: ElementRef,
    private modalController: ModalController,
    private practiceBotService: PracticeBotService,
  ) {}

  ngOnInit() {
    this.isMobile = this.appState.get('isMobile');
    const content = this.habit?.screen?.content || {};
    const rawText = content?.text ?? '';
    this.innerTextHtml = this._sanitizer.bypassSecurityTrustHtml(rawText);
    this.currentAction = {
      title: content.title || '',
      description: rawText,
    };
    this.baseAction = { ...this.currentAction };
    this.loadPracticeAttempts();

    // When the parent page leaves (ionViewWillLeave on daily-steps-content-mobile-v2),
    // cancel any in-flight HTTP request and stop the stream interval so Angular's zone
    // is clean by the time the user can open the foozi-ai modal.
    this.microActionState.onPageLeave$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.httpCancel$.next();
        if (this.activeStreamInterval !== null) {
          clearInterval(this.activeStreamInterval);
          this.activeStreamInterval = null;
        }
        if (this.isSending) {
          this.isSending = false;
          const last = this.chat[this.chat.length - 1];
          if (last?.isStreaming) last.isStreaming = false;
        }
      });

    this.registerKeyboardEvents();

    // Always start with the normal action card + history.
    // The recommit prompt is shown on-demand only when the user explicitly clicks a chip
    // on an already-completed action — not automatically on every open.
    this.chat.push({
      role: 'coach',
      text: '',
      action: { ...this.currentAction },
      descriptionHtml: this.innerTextHtml,
      showChips: true,
    });

    // On re-open: confirm prior action via API, then restore chat.
    // Priority: (1) local sessionStorage, (2) server messages via checkSessionValidity,
    // (3) showPriorActionAsPending fallback.
    this.isLoading = true;
    this.initSyncPromise = this.ensureSessionId().then(sessionId => {
      if (!sessionId) {
        this.isLoading = false;
        this.cdRef.detectChanges();
        return;
      }
      const scid = (this.habit?.screen?.id || '').toString();
      return new Promise<void>(resolve => {
        // The latest action comes from the get-or-create that resolved this thread a moment ago,
        // so only the transcript is still worth a request. The transcript is fetched either way, so
        // clearing the sessionStorage chat never loses the conversation.
        of(this.resolvedAction)
          .pipe(
            switchMap((existing: any) => {
              // The transcript restores the chat when the sessionStorage cache has been cleared, and
              // it comes from the same get-or-create — see transcriptFor, which only goes to the
              // server for a payload that did not carry it.
              return this.transcriptFor(scid, sessionId, existing).pipe(
                map((sessionRes: any) => ({ existing, sessionRes }))
              );
            }),
            takeUntil(this.destroyed$),
            finalize(() => resolve())
          )
          .subscribe(({ existing, sessionRes }: any) => {
            const body = existing
              ? ((existing?.message && typeof existing.message === 'object') ? existing.message : existing)
              : null;
            const existingAction = body?.action || null;

            // Compute server messages and resolved status once — shared by both the
            // existingAction path and the no-action fallback, so both display the
            // correct chips regardless of which code path runs.
            const allMessages: any[] = sessionRes?.messages || [];
            const serverMessages = allMessages.filter(m => {
              if (m.role === 'assistant') return false;
              // Include messages that have no screen_content_id OR an exact match.
              // The server may not propagate the scid from the customize URL to the
              // stored message record, so we must not require it. Exclude only messages
              // that carry a DIFFERENT explicit scid (another screen's micro-action).
              if (m.screen_content_id && String(m.screen_content_id) !== scid) return false;
              return true;
            });

            // A thread with turns in it has been opened before, so its opening event is already
            // written. Keyed on the transcript, not on the action: the flag used to be set only
            // where the answer also carried one, so a screen reopened without it wrote a second
            // action_initial on the next refine — and a third on the one after that.
            if (allMessages.length > 0) { this.initialEventFired = true; }


            // Derive acceptance status from event types when server provides no status.
            // We look at the LAST action-related event, not just whether any saved event
            // exists: a coach action_customize that arrives AFTER an action_customization_saved
            // means the user refined again and the action is pending once more.
            const rawStatus = (body?.status || existing?.status || '').toLowerCase();
            let resolvedStatus = rawStatus;
            if (!resolvedStatus) {
              const lastActionEvent = [...serverMessages]
                .reverse()
                .find((m: any) =>
                  m.type === 'action_customization_saved' ||
                  (m.type === 'action_customize' && m.role === 'coach')
                );
              if (lastActionEvent?.type === 'action_customization_saved') {
                resolvedStatus = 'accepted';
              } else if (lastActionEvent?.type === 'action_customize') {
                resolvedStatus = 'refining';
              }
            }

            if (existingAction) {
              const priorDesc = existingAction.description || '';
              this.currentAction = { title: existingAction.title, description: priorDesc };
              this.innerTextHtml = this._sanitizer.bypassSecurityTrustHtml(priorDesc);
              this.sessionSynced = true;
              this.initialEventFired = true; // prior session already stored the initial event

              // Server messages are always authoritative — rebuild from them so refinements
              // made in the foozi-ai component are visible when switching back here.
              // Local sessionStorage cache can be stale and is only a fallback when the
              // server returns no history.
              if (serverMessages.length > 0) {
                this.rebuildChatHistory(serverMessages, existingAction, priorDesc, resolvedStatus);
              } else if (!this.loadChatHistory(sessionId, scid)) {
                // No server messages and no local cache — surface action as pending for review.
                this.showPriorActionAsPending(existingAction, priorDesc, resolvedStatus || 'refining');
              }

              this.pushStateToService();
              this.cdRef.detectChanges();
              setTimeout(() => this.scrollToBottom(), 500);
              return;
            }

            // No existing action from API — still try to restore from server messages.
            // Always prefer server data over local cache; derive the action reference
            // from the last coach action_customize event so chips show correctly.
            if (serverMessages.length > 0) {
              const lastCoachMsg = [...serverMessages].reverse()
                .find((m: any) => m.role === 'coach' && m.action);
              if (lastCoachMsg?.action) {
                this.currentAction = {
                  title: lastCoachMsg.action.title,
                  description: lastCoachMsg.action.description || '',
                };
                this.innerTextHtml = this._sanitizer.bypassSecurityTrustHtml(this.currentAction.description);
              }
              this.rebuildChatHistory(
                serverMessages,
                this.currentAction,
                this.currentAction.description,
                resolvedStatus,
              );
              this.cdRef.detectChanges();
              setTimeout(() => this.scrollToBottom(), 500);
            } else if (!this.loadChatHistory(sessionId, scid)) {
              // Neither the thread nor its transcript knows about a customisation. That is the
              // normal state for an action never customised — and also for one customised BEFORE
              // the chat/customisation split, whose turns stayed in the conversation they were
              // typed into and which no thread lookup can reach. /customizations is keyed by the
              // action rather than by a session, so it survives that split: it can still say what
              // the learner's wording currently is, even when the conversation that produced it
              // cannot be found.
              this.restoreCustomisedWording(scid);
            }

            // Whether or not the server had a customisation to report. The state carries the ids
            // and switches a commit, a completion and Open Tool are run from, and those belong to
            // the SCREEN — an action nobody has customised yet is no less committable. Published
            // only from the branch above, an action opened from History left the state null and
            // those three chips did nothing at all when tapped.
            this.pushStateToService();
          });
      });
    }).then(() => {
      this.isLoading = false;
      this.cdRef.detectChanges();
    }).catch(() => {
      this.isLoading = false;
      this.cdRef.detectChanges();
    });
  }

  ngAfterViewInit() {
    setTimeout(() => this.scrollToBottom(), 500);
    setTimeout(() => this.initChatScrollObservers(), 500);
  }
  private initChatScrollObservers(): void {
    const el = this.chatScrollEl?.nativeElement;
    if (!el) return;
    this.checkChatScrollable();
    this.chatMutationObserver?.disconnect();
    this.chatMutationObserver = new MutationObserver(() => this.checkChatScrollable());
    this.chatMutationObserver.observe(el, { childList: true, subtree: true });
  }

  checkChatScrollable(): void {
    const el = this.chatScrollEl?.nativeElement;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    this.showScrollToBottom = distanceFromBottom > this.BOTTOM_THRESHOLD;
    this.cdRef.detectChanges();
  }

  // ─── Recommit flow ────────────────────────────────────────────────────────

  // True when the action is confirmed completed in todoActions (screenContentId
  // match + userActionCompletion=true). Used in the template to disable the chip.
  get isCompletedInTodoList(): boolean { return !!this.findMatchingTodoAction()?.userActionCompletion; }

  private findMatchingTodoAction(): any | null {
    const screenContentId = this.habit?.screen?.id;
    if (!screenContentId) return null;
    const todoActions: any[] = this.appState.get('todoActions') || [];
    const matches = todoActions.filter(a => String(a.screenContentId) === String(screenContentId));
    if (!matches.length) return null;
    return matches.reduce((best, a) => (Number(a.id) > Number(best.id) ? a : best), matches[0]);
  }

  /**
   * Hands the host the customised wording for the calendar entry, without touching the fields the
   * action itself is rendered from.
   *
   * It used to write straight over habit.screen.content.title/text. That object is the step's own
   * screen content — shared with everything that renders the step and cached in appState — so the
   * customisation leaked out of this screen and stayed there until the step was fetched again. Two
   * things followed: the starting action read back as the customised one, and a reopen re-derived
   * baseAction from those fields, which sent the learner's own wording to the server as the
   * action's original.
   */
  private publishCustomizedActionForCommit(): void {
    const content = this.habit?.screen?.content;
    if (!content) return;
    if (this.currentAction.title) {
      content.customizedTitle = this.currentAction.title;
    }
    if (this.currentAction.description !== undefined) {
      content.customizedText = this.currentAction.description;
    }
  }

  private formatActionCompletionDate(action: any): string {
    const iso = action?.actionCompletionDateTime;
    if (!iso) return '';
    try {
      // The learner's language, not the browser's — see I18n.locale.
      return new Date(iso).toLocaleString(this.i18n.locale, {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit',
      });
    } catch { return ''; }
  }

  // Pushes a system event bubble to the local chat, saves to sessionStorage,
  // and appends to the server so it persists on reopen in both components.
  private buildSystemEventContent(type: 'action_committed' | 'action_completed'): string {
    const title = this.currentAction?.title || '';
    const escapedTitle = title.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const titleHtml = escapedTitle ? `<span class="system-action-highlight">${escapedTitle}</span>` : '';
    return type === 'action_committed'
      ? (title ? `Action: ${titleHtml} committed to your schedule` : 'Action committed to your schedule')
      : (title ? `Action: ${titleHtml} marked as complete` : 'Action marked as complete');
  }

  private appendSystemEventToServer(type: 'action_committed' | 'action_completed', content: string): void {
    if (!this.sessionId) return;
    this.fooziService.appendSessionEvent(this.sessionId, {
      role: 'system',
      type,
      content,
      screen_content_id: (this.habit?.screen?.id || '').toString(),
    }).pipe(takeUntil(this.destroyed$)).subscribe();
  }

  /**
   * Opens the practice bot for this micro-action, same as the "Practice" chip in
   * the to-do list (my-commitments) — same modal styling and dismiss handling.
   */
  async openPracticeBot(event: Event, action?: CoachAction) {
    event?.stopPropagation();
    // Every practice call — defaults, refine, session, history — is keyed on the micro-action's
    // screen_content_id. A CoachAction carries only title and description, so the id is taken
    // from the habit and sent alongside them; without it the modal opens inert.
    const practiceAction = {
      ...(action || this.currentAction),
      screenContentId: this.habit?.screen?.id,
    };
    const modal = await this.modalController.create({
      component: PracticeBotModalComponent,
      componentProps: { action: practiceAction },
      cssClass: this.isMobile ? 'pratice-bot-custom-modal' : 'web-daily-steps-custom-modal web-pratice-bot-custom-modal',
    });
    modal.onDidDismiss().then((modalDataResponse) => {
      // A session completed inside the modal is only visible here once the history is re-read.
      this.loadPracticeAttempts();
      const data = modalDataResponse?.data;
      // Committing an action goes through the parent's calendar flow, same as the commit chip.
      if (data?.practiceIntent === 'reschedule') {
        // Practice stays available on a read-only screen — it writes nothing outside the action —
        // but this is a commit, which the host cannot carry for a past cycle. Said out loud rather
        // than dropped: the learner pressed something and deserves to know why it did nothing.
        if (this.readOnly) {
          this.toasterService.error(
            this.i18n.t('Switch to that cycle to commit this action.'),
          );
          return;
        }
        this.onCommitChipClick();
        return;
      }
      if (data?.actionCompleted) {
        this.refreshTodoActions();
      }
    });
    return await modal.present();
  }

  /**
   * The completed practice attempts for this micro-action. Each one gets a "Practice Session
   * Completed" card whose View Feedback opens that attempt's report.
   */
  private loadPracticeAttempts(offset = 0, chasesLeft = this.PRACTICE_FILL_CHASES) {
    const screenContentId = this.habit?.screen?.id;
    if (!screenContentId || !this.canVoicePractice) { return; }
    // Supersessions from earlier visits, which this instance's set knows nothing about.
    if (!offset) {
      this.practiceBotService
        .getSupersededAttemptIds(screenContentId)
        .forEach((id) => this.supersededAttemptIds.add(id));
    }
    // Two reads of the same action can finish out of order — the initial one after the refresh that
    // follows a completed session — and the older answer would drop the new attempt from the list.
    const requestId = ++this.practiceHistoryRequestId;
    this.practiceBotService.getPracticeHistory(screenContentId, {
      limit: this.PRACTICE_PAGE_SIZE,
      offset,
    })
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (response) => {
          if (requestId !== this.practiceHistoryRequestId) { return; }   // a newer read superseded this
          // The request for the previous action stays in flight when the input switches to a new
          // one. Its attempts belong to a different micro-action, so applying them here would put
          // another action's sessions on this card — and open its report from "View Feedback". Kept
          // alongside the sequence check, which cannot see a switch that issues no request at all.
          if (this.habit?.screen?.id !== screenContentId) { return; }

          const completed = this.practiceBotService.completedAttemptsFrom(response);
          // Every attempt the page held, showable or not: a short page is the end of the history,
          // and there is nothing further back to look through.
          const pageLength = (this.practiceBotService.attemptsFrom(response) || []).length;
          if (!completed.length && pageLength >= this.PRACTICE_PAGE_SIZE && chasesLeft > 0) {
            // Nothing on this page the card can show. Keep what is on screen and look further back
            // rather than clearing it for a page that only happens to hold no completed attempt.
            this.loadPracticeAttempts(offset + pageLength, chasesLeft - 1);
            return;
          }

          this.practiceAttempts = completed;
          // The card belongs where the practice happened: after whatever had been said by then,
          // so a customisation that follows it reads below it rather than above.
          this.practiceCardAfterCount = this.chat.length;
          this.cdRef.detectChanges();
        },
        (error) => console.log('Practice history unavailable', error)
      );
  }

  /** "View Feedback" — opens the practice bot already showing that attempt's report. */
  async openPracticeFeedback(event: Event, attempt: PracticeCompletedAttempt) {
    event?.stopPropagation();
    if (!attempt?.id) { return; }
    const modal = await this.modalController.create({
      component: PracticeBotModalComponent,
      componentProps: {
        action: { ...this.currentAction, screenContentId: this.habit?.screen?.id },
        openSummarySessionId: attempt.id,
      },
      cssClass: this.isMobile ? 'pratice-bot-custom-modal' : 'web-daily-steps-custom-modal web-pratice-bot-custom-modal',
    });
    // Practising again from inside the report adds an attempt, so re-read the list on the way out.
    modal.onDidDismiss().then(() => this.loadPracticeAttempts());
    return await modal.present();
  }

  /**
   * "Monday, August 3rd at 9:50 AM", as the card is designed — in the learner's language.
   *
   * Every part comes from the language they picked, not the browser's: the weekday and month from
   * Intl, the word between date and time from the catalog. A translation is free to put the time
   * first, which is why it is one interpolated string rather than two halves joined here.
   *
   * The ordinal is English's alone. Other languages number the day plainly, and asking Intl for the
   * whole date gives each one its own order and punctuation — "3 de agosto de 2025", not a
   * word-for-word English date.
   */
  practiceAttemptLabel(at: string): string {
    if (!at) { return ''; }
    const when = new Date(at);
    if (isNaN(when.getTime())) { return ''; }
    const locale = this.i18n.locale;
    const time = when.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
    const day = when.getDate();
    const date = locale === 'en'
      ? `${when.toLocaleDateString(locale, { weekday: 'long' })}, `
        + `${when.toLocaleDateString(locale, { month: 'long' })} ${day}${this.ordinalSuffix(day)}`
      : when.toLocaleDateString(locale, { weekday: 'long', month: 'long', day: 'numeric' });
    return this.i18n.t('%{date} at %{time}', { date, time });
  }

  /** 1st / 2nd / 3rd / 4th — including the 11th-13th exceptions. English only; see above. */
  private ordinalSuffix(day: number): string {
    if (day % 100 >= 11 && day % 100 <= 13) { return 'th'; }
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  }

  /**
   * Only the most recent attempt gets a card. The chat scrolls to the bottom on every new
   * message, so a card per attempt would stack under the conversation and push the action the
   * learner just customised out of view. The full list lives in the practice modal itself.
   */
  /**
   * The message the completed-practice card is rendered after, so it sits where the practice
   * happened instead of being pinned below the whole conversation.
   */
  /**
   * Re-take the placement once the conversation itself is known. loadPracticeAttempts() runs from
   * ngOnInit, so on a re-open its response normally lands while `chat` still holds nothing but the
   * intro bubble — the count would be 1, and rebuildChatHistory()/loadChatHistory() would then
   * replace the array, leaving the card rendered after the FIRST restored message. Called at the end
   * of both restore paths, which is the last point that had been said before the reload.
   */
  private anchorPracticeCardToChatEnd() {
    this.practiceCardAfterCount = this.chat.length;
  }

  get practiceCardAfterIndex(): number {
    const last = this.chat.length - 1;
    if (this.practiceCardAfterCount <= 0) { return last; }
    return Math.min(this.practiceCardAfterCount - 1, last);
  }

  /**
   * True when this attempt practised wording the learner has since customised. The card still
   * shows — the report exists — but it says which version it was.
   */
  isEarlierVersionAttempt(attempt: PracticeCompletedAttempt): boolean {
    return !!attempt?.id && this.supersededAttemptIds.has(attempt.id);
  }

  get latestPracticeAttempt(): PracticeCompletedAttempt | null {
    return this.practiceAttempts[this.practiceAttempts.length - 1] ?? null;   // oldest first
  }

  // Re-pulls todoActions so the chips reflect a completion made inside the practice bot.
  private refreshTodoActions() {
    const pathId = this.appState.get('currentPath')?.id;
    if (!pathId) return;
    this.kyperApiService.getActionsList({ 'path_id': pathId })
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (data) => {
          if (data?.actions) {
            this.appState.set('todoActions', data.actions);
          }
          this.cdRef.detectChanges();
        },
        () => {},
      );
  }

  onCommitChipClick() {
    // The chips are hidden on a read-only screen, but hiding a control is not the same as closing
    // the path to it — the practice modal's "reschedule" dismiss reaches this too. Guarded at the
    // write rather than at each way in.
    if (this.readOnly) { return; }
    this.publishCustomizedActionForCommit();
    const content = this.buildSystemEventContent('action_committed');
    this.commitClicked.emit(() => {
      this.chat.push({ role: 'system', text: content });
      setTimeout(() => this.scrollToBottom(), 100);
      this.saveChatHistory();
      this.appendSystemEventToServer('action_committed', content);
    });
  }

  // Shows the recommit alert only when the action is completed AND present in
  // todoActions with userActionCompletion=true. Otherwise proceeds directly.
  showRecommitPrompt(action: 'commit' | 'complete') {
    if (this.readOnly) { return; }   // the other way into a commit or a completion — see above
    const matched = this.findMatchingTodoAction();
    if (!(matched?.userActionCompletion)) {
      if (action === 'commit') {
        this.onCommitChipClick();
      } else {
        const content = this.buildSystemEventContent('action_completed');
        this.markCompletedClicked.emit(() => {
          this.chat.push({ role: 'system', text: content });
          setTimeout(() => this.scrollToBottom(), 100);
          this.saveChatHistory();
          this.appendSystemEventToServer('action_completed', content);
        });
      }
      return;
    }
    const completedAt = this.formatActionCompletionDate(matched);
    this.toasterService.showRecommitConfirm(
      () => this.executeRecommit(action,matched),
      () => {},
      completedAt,
    );
  }

  private executeRecommit(action: 'commit' | 'complete',matchedAction) {
    this.isRecommitMode = true;
    this.inputDisabled = false;
    const actionId = this.habit?.screen?.id;

    if (action === 'commit') {
      const content = this.buildSystemEventContent('action_committed');
      const emitCallback = () => {
        this.publishCustomizedActionForCommit();
        this.commitClicked.emit(() => {
          this.chat.push({ role: 'system', text: content });
          setTimeout(() => this.scrollToBottom(), 100);
          this.saveChatHistory();
          this.appendSystemEventToServer('action_committed', content);
        });
      };
      if (!actionId) {
        emitCallback();
        return;
      }
      this.routineV2Service.repeatUserAction({ action_id: matchedAction?.id })
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: () => {
            const pathId = this.appState.get('currentPath')?.id;
            const requestData = { 'path_id': pathId };
            // Reset completion state on the server before handing off to the parent,
            // so habitScheduleLater's "already completed" guard does not block.
            // this.kyperApiService.schedule_commit_action_kyper({
            //   step_id: this.habit?.stepId,
            //   screen_id: this.habit?.screenId,
            //   screen_content_id: this.habit?.screen?.id,
            //   user_action_scheduled_later: false,
            //   user_action_completion: false,
            //   customization_id:matchedAction?.customizationId
            //   // action_date_time: this.kyperApiService.formatDate(),
            // }).pipe(takeUntil(this.destroyed$)).subscribe({
            //   next: (res: any) => {
                if (this.habit?.screen?.userResponse) {
                  this.habit.screen.userResponse.userActionCompletion = false;
                }
                if (pathId) {
                  this.kyperApiService.getActionsList(requestData)
                    .pipe(takeUntil(this.destroyed$))
                    .subscribe(
                      (data) => { this.appState.set('todoActions', data?.actions); emitCallback(); },
                      () => emitCallback(),
                    );
                } else {
                  emitCallback();
                }
            //   },
            // });
          },
          error: () => this.toasterService.error(this.i18n.t('Failed to reset action. Please try again.')),
        });
    } else {
      const content = this.buildSystemEventContent('action_completed');
      const emitCallback = () => {
        this.publishCustomizedActionForCommit();
        this.markCompletedClicked.emit(() => {
          this.chat.push({ role: 'system', text: content });
          setTimeout(() => this.scrollToBottom(), 100);
          this.saveChatHistory();
          this.appendSystemEventToServer('action_completed', content);
        });
      };
      if (!actionId) {
        emitCallback();
        return;
      }
      const proceed = () => {
        const pathId = this.appState.get('currentPath')?.id;
        const requestData = { 'path_id': pathId };
        // Reset completion state on server before handing off to parent,
        // so habitConfirm's "already completed" guard does not block.
        // this.kyperApiService.schedule_commit_action_kyper({
        //   step_id: this.habit?.stepId,
        //   screen_id: this.habit?.screenId,
        //   screen_content_id: this.habit?.screen?.id,
        //   user_action_scheduled_later: false,
        //   user_action_completion: false,
        //   customization_id:matchedAction?.customizationId
        //   // action_date_time: this.kyperApiService.formatDate(),
        // }).pipe(takeUntil(this.destroyed$)).subscribe({
        //   next: (res: any) => {
            if (this.habit?.screen?.userResponse) {
              this.habit.screen.userResponse.userActionCompletion = false
            }
            if (pathId) {
              this.kyperApiService.getActionsList(requestData)
                .pipe(takeUntil(this.destroyed$))
                .subscribe(
                  (data) => { this.appState.set('todoActions', data?.actions); emitCallback(); },
                  () => emitCallback(),
                );
            } else {
              emitCallback();
            }
        //   },
        // });
      };
      this.routineV2Service.repeatUserAction({ action_id: matchedAction?.id })
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: () => proceed(),
          error: () => this.toasterService.error(this.i18n.t('Failed to reset action. Please try again.')),
        });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['habit']) {
      const rawDesc = (typeof this.currentAction?.description === 'string'
        ? this.currentAction.description
        : null) ?? this.habit?.screen?.content?.text ?? '';
      this.innerTextHtml = this._sanitizer.bypassSecurityTrustHtml(rawDesc);
      this.currentAction = { ...this.currentAction, description: rawDesc };
      setTimeout(() => this.changeSubTextFont());
      this.reloadPracticeStateForNewAction(changes['habit']);
    }
  }

  /**
   * Practice history belongs to one micro-action. When the parent swaps the action without
   * recreating this component, the loaded attempts — and the ids marked superseded against the
   * PREVIOUS action's wording — are no longer about what is on screen, so they are dropped and
   * re-fetched. Guarded on the screen id so re-emitting the same action costs nothing, and on
   * firstChange because ngOnInit already loads that one.
   */
  private reloadPracticeStateForNewAction(change: SimpleChange) {
    if (change.firstChange) { return; }
    if (change.previousValue?.screen?.id === change.currentValue?.screen?.id) { return; }
    this.practiceAttempts = [];
    this.supersededAttemptIds.clear();
    this.loadPracticeAttempts();
  }

 sanitize(html: string): SafeHtml {
    return this._sanitizer.bypassSecurityTrustHtml(html || '');
  }

 changeSubTextFont() {
    if (this.stepTitle) {
      const mainElement = this.stepTitle.nativeElement;
      this.applyStylesToAllInnerTags(mainElement);
    }
  }

  applyStylesToAllInnerTags(element: HTMLElement) {
    if (!element) { return; }
    const childElements = Array.from(element.children);
    childElements.forEach((childElement: Element) => {
      const tagName = childElement.tagName.toLowerCase();

      if (tagName === 'ol' || tagName === 'ul') {
        this.render.setStyle(childElement, 'list-style', 'none');
        this.render.setStyle(childElement, 'padding-left', '1.5em');
      }

      if (tagName === 'li') {
        const dataList = childElement.getAttribute('data-list');
        if (dataList === 'bullet') {
          this.render.setStyle(childElement, 'list-style-type', 'disc');
          this.render.setStyle(childElement, 'display', 'list-item');
          this.render.setStyle(childElement, 'list-style-position', 'outside');
        } else if (dataList === 'ordered') {
          this.render.setStyle(childElement, 'list-style-type', 'decimal');
          this.render.setStyle(childElement, 'display', 'list-item');
          this.render.setStyle(childElement, 'list-style-position', 'outside');
        }
      }

      this.applyStylesToAllInnerTags(childElement as HTMLElement);
    });
  }
  // ─── Session bootstrap — lazy, called only on first user send ────────────

  /**
   * This micro-action's transcript: its own customisation thread, read by id.
   *
   * /actions/{scid}/thread is deliberately not consulted — it answers 404 in practice, and it would
   * only ever carry ACTION turns anyway, leaving out the committed and completed notices, which are
   * recorded as system events on the thread itself.
   *
   * Work done before the chat/customisation split stays in the conversation it was typed into and
   * is not reachable from here at all; restoreCustomisedWording recovers the action's current
   * wording from /customizations, which is keyed by the action rather than by a session.
   */
  private actionTranscript(scid: string, sessionId: string): Observable<any> {
    if (!sessionId) { return of(null); }
    return this.fooziService.checkSessionValidity(sessionId).pipe(catchError(() => of(null)));
  }

  /**
   * This thread's messages, from the answer that resolved it wherever that answer holds them.
   *
   * POST /session?screen_content_id=X is get-or-create and returns the session — transcript
   * included — so reading the same session again straight afterwards is the identical request
   * twice. The fetch is kept for a payload that arrives without messages, so a server answering
   * with the id alone still restores the conversation rather than showing an empty screen.
   */
  private transcriptFor(scid: string, sessionId: string, resolved: any): Observable<any> {
    if (Array.isArray(resolved?.messages)) { return of(resolved); }
    return this.actionTranscript(scid, sessionId);
  }

  private ensureSessionId(): Promise<string | null> {
    if (this.sessionId) return Promise.resolve(this.sessionId);
    if (this.sessionIdPromise) return this.sessionIdPromise;

    // A customisation thread, not a conversation. The two are separate kinds of session, resolved
    // by different keys: this one by the action. Before the split they shared `foozi_session`, so
    // opening this screen could drop the Coach panel's conversation and vice versa.
    //
    // Deliberately no local fallback. A cached id may predate the split and point at a plain chat
    // session, and reusing it would keep writing this action's turns into the learner's
    // conversation. The POST is get-or-create, so asking the server is both correct and cheap.
    const scid = this.habit?.screen?.id;
    if (scid === undefined || scid === null || scid === '') {
      return Promise.resolve(null);
    }
    const stored = sessionStorage.getItem(this.sessionStorageKey);

    this.sessionIdPromise = new Promise(resolve => {
      // createChatSession rather than resolveActionSession: the same POST, but the whole answer.
      // Get-or-create already returns the action as the server last stored it, and resolveActionSession
      // keeps only the id — which had this screen ask a second request for what it had just been told.
      // show_default_questions false: this is a customisation thread, never a chat to open with an
      // introduction, and the flag defaults to TRUE when a call says nothing about it.
      this.fooziService.createChatSession(scid, false)
        .pipe(catchError(() => of(null)), takeUntil(this.destroyed$))
        .subscribe((created: any) => {
          const sessionId = created?.session_id || '';
          // Held for ngOnInit, which reads the latest action off it instead of fetching one.
          this.resolvedAction = created || null;
          this.sessionIdPromise = null;
          if (!sessionId) {
            resolve(null);
            return;
          }
          if (stored && stored !== sessionId) {
            // A different thread than last time — a second device, or a cached id from before the
            // split. The local transcript cache belongs to the old one.
            Object.keys(sessionStorage)
              .filter(k => k.startsWith('ma_chat_'))
              .forEach(k => sessionStorage.removeItem(k));
            sessionStorage.removeItem('ma_state');
          }
          this.sessionId = sessionId;
          this.storeSessionId(sessionId);
          mixpanel.track('Micro-action Coach: Action thread resolved', {
            session_id: sessionId,
            screen_content_id: scid,
            resumed: sessionId === stored,
          });
          resolve(sessionId);
        });
    });

    return this.sessionIdPromise;
  }

  /**
   * Restores the learner's latest customised wording from /customizations.
   *
   * A last resort for work that predates the split: the turns are unreachable, but the action's
   * current title and description are not, so the screen shows what they last made rather than
   * silently reverting to the authored wording.
   */
  private restoreCustomisedWording(scid: string): void {
    if (!scid) { return; }
    this.fooziService.listCustomizations(scid, 1, 0)
      .pipe(catchError(() => of(null)), takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        // The endpoint answers with a bare array, newest first, one row per action.
        const rows = Array.isArray(response)
          ? response
          : (response?.customizations ?? response?.items ?? []);
        const latest = Array.isArray(rows) ? rows[0] : null;
        if (!latest?.title) { return; }

        this.currentAction = { title: latest.title, description: latest.description || '' };
        this.innerTextHtml = this._sanitizer.bypassSecurityTrustHtml(this.currentAction.description);
        this.showPriorActionAsPending(
          { title: latest.title, description: latest.description || '' },
          latest.description || '',
          latest.is_committed ? 'accepted' : 'refining',
        );
        this.pushStateToService();
        this.cdRef.detectChanges();
      });
  }

  /**
   * Where this action's thread id is remembered. Per action, and deliberately NOT `foozi_session` —
   * that key is the Coach panel's conversation, and the two must not overwrite each other.
   */
  private get sessionStorageKey(): string {
    const scid = this.habit?.screen?.id;
    return scid ? `foozi_action_session_${scid}` : 'foozi_action_session';
  }

  private storeSessionId(sessionId: string): void {
    sessionStorage.setItem(this.sessionStorageKey, sessionId);
    sessionStorage.setItem(`${this.sessionStorageKey}_created`, Date.now().toString());
  }


  // ─── User actions ─────────────────────────────────────────────────────────

  sendChip(chip: string) {
    this.doSend(chip, this.resolvePreset(chip));
  }

  sendUserMessage() {
    const text = this.userInput.trim();
    if (!text) return;

    // If the action is already completed and the user hasn't recommitted yet,
    // show the alert before sending. Text stays in the input on "No".
    const matchedAction = this.isActionCompleted ? this.findMatchingTodoAction() : null;
    console.log('sendUserMessage matchedAction', matchedAction);
    if (matchedAction?.userActionCompletion) {
      const completedAt = this.formatActionCompletionDate(matchedAction);
      const preset = this.pendingPresetKey;
      this.toasterService.showRecommitConfirm(
        () => {
          this.userInput = '';
          this.pendingPresetKey = null;
          this.executeRecommitCustomize(text, preset, matchedAction);
        },
        () => { this.userInput = ''; },
        completedAt,
      );
      return;
    }

    this.userInput = '';
    const preset = this.pendingPresetKey;
    this.pendingPresetKey = null;
    this.doSend(text, preset);
  }

  private executeRecommitCustomize(text: string, preset: string | null,matchedAction) {
    this.isRecommitMode = true;
    this.inputDisabled = false;
    const actionId = this.habit?.screen?.id;
    const doSend = () => this.doSend(text, preset);

    if (!actionId) {
      doSend();
      return;
    }

    this.routineV2Service.repeatUserAction({ action_id: matchedAction?.id })
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          const pathId = this.appState.get('currentPath')?.id;
          // this.kyperApiService.schedule_commit_action_kyper({
          //   step_id: this.habit?.stepId,
          //   screen_id: this.habit?.screenId,
          //   screen_content_id: this.habit?.screen?.id,
          //   user_action_scheduled_later: false,
          //   user_action_completion: false,
          //   customization_id:matchedAction?.customizationId
          //   // action_date_time: this.kyperApiService.formatDate(),
          // }).pipe(takeUntil(this.destroyed$)).subscribe({
          //   next: () => {
            if (this.habit?.screen?.userResponse) {
              this.habit.screen.userResponse.userActionCompletion = false
            }
              if (pathId) {
                this.kyperApiService.getActionsList({ 'path_id': pathId })
                  .pipe(takeUntil(this.destroyed$))
                  .subscribe({
                    next: (data) => { this.appState.set('todoActions', data?.actions); doSend(); },
                    error: () => doSend(),
                  });
              } else {
                doSend();
              }
          //   },
          //   error: doSend,
          // });
        },
        error: () => this.toasterService.error(this.i18n.t('Failed to reset action. Please try again.')),
      });
  }

  // Persists the accepted action to the backend and appends the server session events.
  // Called automatically after the API response streams in — no user interaction needed.
  private acceptActionInline(accepted: CoachAction, descHtml: SafeHtml): void {
    // Earlier attempts practised different wording, so their card no longer belongs to this action.
    // Hidden here, but only WRITTEN once the customisation is saved (see the tap below): a failed
    // save leaves the action on its old wording, where those attempts are valid again.
    // The card stays — that session happened and its report is still worth reaching — but it is
    // marked so it cannot read as feedback on the wording the learner has just changed.
    const superseded = this.practiceAttempts.map((attempt) => attempt.id).filter(Boolean);
    superseded.forEach((id) => this.supersededAttemptIds.add(id));
    this.currentAction = accepted;
    this.innerTextHtml = descHtml;
    this.isActionAccepted = true;
    this.actionUpdated.emit({ ...accepted });
    this.pushStateToService();

    this.fooziService.saveActionCustomization({
      screen_content_id: this.habit?.screen?.id,
      step_id: this.habit?.stepId,
      screen_id: this.habit?.screenId,
      user_action_completion: !!this.habit?.screen?.userResponse?.userActionCompletion,
      title: accepted.title,
      description: accepted.description,
      base_title: this.baseAction.title,
      base_description: this.baseAction.description,
    }).pipe(
      takeUntil(this.destroyed$),
      // Only a saved customisation supersedes anything, so only a real emission writes the ids.
      // `catchError` below turns a failure into an emission too, which is why this sits above it.
      tap(() => this.practiceBotService.addSupersededAttemptIds(this.habit?.screen?.id, superseded)),
      catchError(() => {
        this.toasterService.error(this.i18n.t('Failed to save action. Please try again.'));
        // The action keeps its old wording, so its attempts belong to it again. Nothing was written,
        // so re-reading the history brings them back.
        superseded.forEach((id) => this.supersededAttemptIds.delete(id));
        this.loadPracticeAttempts();
        return of(null);
      }),
    ).subscribe();
  }

  // ─── Refine call ──────────────────────────────────────────────────────────

  private doSend(text: string, presetKey: string | null) {
    if (this.isSending || this.inputDisabled || this.readOnly) return;

    // Mark as engaged on first user message so action chips hide
    const snap = this.microActionState.snapshot;
    if (snap && !snap.engaged) {
      this.microActionState.update({ ...snap, engaged: true });
    }

    this.chat.forEach(b => {
      b.showChips = false;
      // Freeze any un-accepted pending action as a historical action card (no chips)
      if (b.showUseThis && b.pendingAction) {
        b.action = { ...b.pendingAction };
        b.descriptionHtml = b.pendingDescHtml;
        b.pendingAction = undefined;
        b.pendingDescHtml = undefined;
      }
      b.showUseThis = false;
    });

    this.chat.push({ role: 'user', text });
    this.isSending = true;
    this.isLoading = true;
    this.microActionState.setProcessing(true);
    this.cdRef.detectChanges();
    this.scrollToBottom();

    const scid = (this.habit?.screen?.id || '').toString();

    this.ensureSessionId().then(sessionId => {
      if (!sessionId) {
        this.isLoading = false;
        this.chat.push({ role: 'coach', text: this.i18n.t('Something went wrong. Please try again.'), isStreaming: false });
        this.isSending = false;
        this.microActionState.setProcessing(false);
        this.cdRef.detectChanges();
        return;
      }

      // Single customize endpoint handles both generate (first send) and refine (subsequent)
      const callCustomize = () => {
        const body: { prompt: string; preset_key?: string } = { prompt: text };
        if (presetKey) body.preset_key = presetKey;

        this.fooziService.customizeMicroAction(sessionId, scid, body)
          .pipe(takeUntil(this.httpCancel$), takeUntil(this.destroyed$))
          .subscribe(
            (res) => {
              this.isLoading = false;
              const msg = (res?.message && typeof res.message === 'object') ? res.message : res;
              const content = msg?.content || '';
              const intentOk = msg?.intent_preserved !== false;
              const newAction: CoachAction | null = intentOk && msg?.action
                ? { title: msg.action.title, description: msg.action.description || '' }
                : null;

              const coachBubble: ChatBubble = { role: 'coach', text: '', isStreaming: true };

              let descHtml: SafeHtml | undefined;
              if (newAction) {
                descHtml = this._sanitizer.bypassSecurityTrustHtml(newAction.description);
                this.isActionAccepted = false;
                //this.pushStateToService();
              }

              this.chat.push(coachBubble);
              this.cdRef.detectChanges();

              this.streamText(content, (partial) => {
                coachBubble.text = partial;
                this.cdRef.detectChanges();
                this.scrollToBottom();
              }, () => {
                coachBubble.isStreaming = false;
                this.isSending = false;
                this.microActionState.setProcessing(false);
                if (newAction) {
                  // Auto-accept the refined action — no intermediate "Use This / Refine Again" step.
                  coachBubble.action = { title: newAction.title, description: newAction.description };
                  coachBubble.descriptionHtml = descHtml;
                  coachBubble.showChips = true;
                  coachBubble.splitLayout = true;
                  this.acceptActionInline(newAction, descHtml!);
                } else {
                  coachBubble.showChips = true;
                }
                this.saveChatHistory();
                this.cdRef.detectChanges();
                this.scrollToBottom();
              });
            },
            (error) => {
              this.isLoading = false;
              const serverDetail = error?.error?.detail || error?.error?.message;
              const displayMsg = serverDetail
                ? this.i18n.t(serverDetail)
                : this.i18n.t('Something went wrong. Please try again.');
              this.chat.push({ role: 'coach', text: displayMsg, isStreaming: false });
              this.isSending = false;
              this.microActionState.setProcessing(false);
              this.cdRef.detectChanges();
              this.scrollToBottom();
            }
          );
      };

      // The opening event: the intro line and the action as it stood before the learner touched it,
      // written once, on the first prompt of a thread that has none. Resolves immediately on every
      // call after that — see the transcript check in ngOnInit for what "after that" means across
      // reopens.
      const maybeFireInitialEvent = (): Promise<void> => {
        if (this.initialEventFired) return Promise.resolve();
        this.initialEventFired = true;
        return new Promise<void>(resolve => {
          this.fooziService.appendSessionEvent(sessionId, {
            role: 'coach',
            type: 'action_initial',
            content: this.introText,
            action: { title: this.currentAction.title, description: this.currentAction.description },
            screen_content_id: scid,
          }).pipe(takeUntil(this.destroyed$), catchError(() => of(null))).subscribe(() => resolve());
        });
      };

      if (this.sessionSynced) {
        // Fire initial event first; customize only after server acknowledges it
        maybeFireInitialEvent().then(() => callCustomize());
      } else {
        // Wait for ngOnInit session check before first send
        (this.initSyncPromise || Promise.resolve()).then(() => {
          this.sessionSynced = true;
          maybeFireInitialEvent().then(() => callCustomize());
        });
      }
    });
  }

  // ─── #2 — Push state so Copilot window can sync ───────────────────────────

  private pushStateToService() {
    if (!this.sessionId) return;
    const prior = this.microActionState.snapshot;
    const rawDesc = typeof this.currentAction.description === 'string'
      ? this.currentAction.description : '';
    this.innerTextHtml = this._sanitizer.bypassSecurityTrustHtml(rawDesc);
    const state: MicroActionSessionState = {
      sessionId: this.sessionId,
      scid: (this.habit?.screen?.id || '').toString(),
      stepId: this.habit?.stepId || '',
      screenId: this.habit?.screenId || '',
      screenContentId: this.habit?.screen?.id || '',
      currentAction: { title: this.currentAction.title, description: rawDesc },
      stepTitle: this.habit?.screen?.content?.title || '',
      engaged: prior?.engaged ?? false,
      canSchedule: this.canSchedule,
      hasToolLink: this.hasToolLink,
      toolLink: this.habit?.screen?.content?.toolLink || '',
      tool: this.habit?.screen?.tool || null,
      canCompleteNow: this.canCompleteNow,
      isActionCompleted: this.isActionCompleted,
      isAccepted: this.isActionAccepted,
    };
    this.microActionState.update(state);
    sessionStorage.setItem('ma_state', JSON.stringify(state));
  }

  // ─── Chat history restoration ─────────────────────────────────────────────

  private rebuildChatHistory(
    messages: any[],
    latestAction: { title: string; description: string },
    latestDesc: string,
    status: string,
  ) {
    // Skip if the recommit prompt is active (completed-action flow is separate)
    if (!this.chat.some(b => b.action)) return;

    const isPendingRefinement = ['pending', 'refining', 'in_progress', 'draft'].includes(status);
    const isAccepted = !isPendingRefinement;
    this.isActionAccepted = true; // always accepted — intermediate refinement step removed
    // The action as it stood before any of this was said.
    //
    // From the path where the path has it: that is the action as published, and it is what the
    // in-step screen shows. Only a screen built from a history row falls back to the thread's own
    // action_initial event — see contentFromHistory for why that content cannot be trusted as the
    // starting point. The event is filtered out of the rendered bubbles below, not discarded.
    // In order of authority: what the host was told outright, then what the thread recorded, then
    // the screen content — which is the action as published for a step in the current cycle, and
    // the LATEST customisation for one opened from History.
    const opening = this.contentFromHistory
      ? messages.find((msg: any) => msg?.type === 'action_initial' && msg?.action)
      : null;
    const stated = this.originalAction;
    const originalTitle = stated?.title || opening?.action?.title
      || this.habit?.screen?.content?.title || '';
    const originalText = stated?.description || opening?.action?.description
      || (this.habit?.screen?.content?.text ?? '');
    if (stated || opening?.action) {
      // What a further customisation reports as the wording it started from. Left as the screen
      // content, a history-opened action would send its own latest version as its base.
      this.baseAction = { title: originalTitle, description: originalText };
    }

    // Keep the introductory text bubble; drop everything else
    const introBubble = this.chat.find(b => b.role === 'coach' && !b.action);
    this.chat = [];
    if (introBubble) this.chat.push(introBubble);

    // Original action card as frozen history (no chips)
    this.chat.push({
      role: 'coach',
      text: '',
      action: { title: originalTitle, description: originalText },
      descriptionHtml: this._sanitizer.bypassSecurityTrustHtml(originalText),
      showChips: false,
    });

    // Filter to only actual micro-action chat messages:
    // - Drop assistant/general-chat messages (role: assistant, or type: 'chat').
    // - Drop action_initial (rendered by the static intro div, never as a bubble).
    // - Keep user and coach messages that have visible text OR carry an action card.
    //   Server action_customize messages may have empty content but a valid action
    //   object — those must not be dropped or the last coach bubble never renders.
    const chatMessages = messages.filter((msg: any) => {
      if (msg.role === 'assistant') return false;
      if (msg.type === 'action_initial') return false;
      if (msg.type === 'chat') return false; // foozi-ai general chat — never affects action chips
      if (!msg.type) return true;
      const text = msg.content || msg.text || '';
      const hasAction = !!(msg.action && msg.action.title);
      if (!text && !hasAction) return false;
      return msg.role === 'user' || msg.role === 'coach' || msg.role === 'system';
    });

    // Find the last coach message index separately — system events that trail
    // the last coach message (e.g. action_committed) must not displace chip placement.
    const lastCoachIdx = chatMessages.reduce(
      (acc: number, msg: any, idx: number) => (msg.role === 'coach' ? idx : acc),
      -1,
    );
    chatMessages.forEach((msg: any, idx: number) => {
      const isLastCoach = idx === lastCoachIdx;
      const isAssistant = msg.role === 'coach';
      const msgText = msg.content || msg.text || '';
      const msgAction: { title: string; description: string } | null = msg.action || null;

      if (!isAssistant) {
        if (msg.role === 'system') {
          this.chat.push({ role: 'system', text: msgText });
          return;
        }
        if (msg.role !== 'user') return;
        this.chat.push({ role: 'user', text: msgText });
      } else {
        const bubble: ChatBubble = {
          role: 'coach',
          text: msgText,
          splitLayout: msg.type === 'action_customization_saved' ? true : undefined,
        };

        if (isLastCoach) {
          // Always show the accepted action card + commit chips on the last coach bubble.
          // Previously-pending refinements are auto-accepted in the new flow.
          bubble.action = { title: latestAction.title, description: latestDesc };
          bubble.descriptionHtml = this.innerTextHtml;
          bubble.showChips = true;
          if (!isAccepted) bubble.splitLayout = true;
        } else if (msgAction) {
          // Intermediate coach message with a customized action — render as frozen
          // history (action card visible, no chips, no accept/decline controls).
          bubble.action = { title: msgAction.title, description: msgAction.description };
          bubble.descriptionHtml = this._sanitizer.bypassSecurityTrustHtml(msgAction.description);
        }

        this.chat.push(bubble);
      }
    });

    // Guard: if no coach bubble set showChips (empty chatMessages or last was a user msg),
    // still show commit chips. Prefer the last coach bubble; fall back to primary card.
    if (!this.chat.some(b => b.showChips)) {
      const lastCoach = [...this.chat].reverse().find(b => b.role === 'coach' && b.action);
      const target = lastCoach || this.chat.find(b => b.action && !b.showUseThis);
      if (target) {
        target.action = { title: latestAction.title, description: latestDesc };
        target.descriptionHtml = this.innerTextHtml;
        target.showChips = true;
      }
    }

    this.anchorPracticeCardToChatEnd();
  }

  // No session message history available — show prior action as pending so user
  // can accept or refine it, rather than incorrectly showing commit chips.
  private showPriorActionAsPending(
    latestAction: { title: string; description: string },
    latestDesc: string,
    status: string,
  ) {
    const isPendingRefinement = ['pending', 'refining', 'in_progress', 'draft'].includes(status);
    const isAccepted = !isPendingRefinement;
    this.isActionAccepted = true; // always accepted — intermediate refinement step removed
    const actionBubble = this.chat.find(b => b.action);
    if (!actionBubble) return;

    if (isAccepted) {
      // Server confirmed acceptance — update card and keep commit chips
      actionBubble.action = { ...this.currentAction };
      actionBubble.descriptionHtml = this.innerTextHtml;
    } else {
      // Previously pending — auto-accept and show chips directly
      actionBubble.showChips = false;
      this.chat.push({
        role: 'coach',
        text: this.i18n.t("Here's where we left off:"),
        action: { title: latestAction.title, description: latestDesc },
        descriptionHtml: this.innerTextHtml,
        showChips: true,
        splitLayout: true,
      });
    }
  }

  // ─── Local chat history persistence (sessionStorage, keyed by session+scid) ──

  private saveChatHistory() {
    if (!this.sessionId) return;
    const scid = (this.habit?.screen?.id || '').toString();
    if (!scid) return;
    const key = `ma_chat_${this.sessionId}_${scid}`;
    const serializable = this.chat.map(b => ({
      role: b.role,
      text: b.text,
      actionTitle: b.action?.title,
      actionDesc: b.action?.description,
      showChips: b.showChips || false,
      splitLayout: b.splitLayout || false,
    }));
    sessionStorage.setItem(key, JSON.stringify(serializable));
  }

  private loadChatHistory(sessionId: string, scid: string): boolean {
    const key = `ma_chat_${sessionId}_${scid}`;
    const raw = sessionStorage.getItem(key);
    if (!raw) return false;
    try {
      const bubbles: any[] = JSON.parse(raw);
      if (!bubbles.length) return false;
      this.chat = bubbles.filter((b: any) => b.role !== 'assistant').map((b: any): ChatBubble => {
        const bubble: ChatBubble = { role: b.role, text: b.text || '' };
        if (b.actionTitle !== undefined || b.actionDesc !== undefined) {
          bubble.action = { title: b.actionTitle || '', description: b.actionDesc || '' };
          bubble.descriptionHtml = this._sanitizer.bypassSecurityTrustHtml(b.actionDesc || '');
        }
        if ((b.pendingTitle !== undefined || b.pendingDesc !== undefined) && !bubble.action) {
          // Backward compat: old sessions stored intermediate actions as pendingTitle/pendingDesc.
          // Promote to action so the card renders (the accept/decline flow no longer exists).
          bubble.action = { title: b.pendingTitle || '', description: b.pendingDesc || '' };
          bubble.descriptionHtml = this._sanitizer.bypassSecurityTrustHtml(b.pendingDesc || '');
          if (b.showUseThis) bubble.showChips = true;
        }
        if (b.showUseThis) bubble.showUseThis = true;
        if (b.showChips) bubble.showChips = true;
        if (b.splitLayout) bubble.splitLayout = true;
        return bubble;
      });
      this.anchorPracticeCardToChatEnd();
      return true;
    } catch {
      return false;
    }
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  private streamText(
    fullText: string,
    onTick: (partial: string) => void,
    onDone?: () => void,
    speed = 6,
    batchSize = 10,
  ) {
    if (!fullText) {
      onDone?.();
      return;
    }
    let i = 0;
    if (this.activeStreamInterval !== null) clearInterval(this.activeStreamInterval);
    this.activeStreamInterval = setInterval(() => {
      if (i < fullText.length) {
        i = Math.min(i + batchSize, fullText.length);
        onTick(fullText.slice(0, i));
      } else {
        clearInterval(this.activeStreamInterval!);
        this.activeStreamInterval = null;
        onDone?.();
      }
    }, speed);
  }

  private resolvePreset(chip: string): string | null {
    const lower = chip.toLowerCase();
    if (lower.includes('simpler')) return 'make_it_simpler';
    if (lower.includes('relevant') || lower.includes('role')) return 'make_it_relevant';
    if (lower.includes('completed') || lower.includes('complete')) return 'I Completed This';
    if (lower.includes('commit')) return 'Commit to This Action';
    return null;
  }

  trackBubble(index: number): number { return index; }

  scrollToBottom() {
    let attempts = 0;
    const tryScroll = () => {
      const el = this.chatScrollEl?.nativeElement;
      if (!el) return;
      const last = el.lastElementChild as HTMLElement | null;
      if (last && el.scrollHeight > el.clientHeight) {
        last.style.scrollMarginBottom = '15px';
        last.scrollIntoView({ block: 'end', behavior: 'smooth' });
        return;
      }
      if (attempts++ < 10) setTimeout(tryScroll, 60);
    };
    requestAnimationFrame(tryScroll);
  }

  onTextareaFocus() {
    this.textareaFocused = true;
    this.cdRef.detectChanges();
  }

  onTextareaBlur() {
    this.textareaFocused = false;
    this.cdRef.detectChanges();
  }

  registerKeyboardEvents() {
    if (this.platform.is('cordova')) {
      this.isCordovaMobile = true;
      this.keyboard.onKeyboardWillShow().pipe(takeUntil(this.destroyed$))
        .subscribe((info: any) => this.moveTextareaUp(info?.keyboardHeight ?? 0));
      this.keyboard.onKeyboardWillHide().pipe(takeUntil(this.destroyed$))
        .subscribe(() => this.resetTextarea());
      return;
    }

    if (!window.visualViewport) return;
    fromEvent(window.visualViewport, 'resize').pipe(takeUntil(this.destroyed$)).subscribe(() => {
      const kh = window.innerHeight - (window.visualViewport?.height ?? window.innerHeight);
      kh > 150 ? this.moveTextareaUp(kh) : this.keyboardShowing && this.resetTextarea();
    });
  }

  moveTextareaUp(keyboardHeight: number) {
    if (this.keyboardShowing) return;
    this.keyboardShowing = true;
    const hostEl = this.elRef.nativeElement as HTMLElement;
    const spaceBelow = window.innerHeight - hostEl.getBoundingClientRect().bottom;
    this.keyboardOffset = Math.max(0, keyboardHeight - spaceBelow - 1);
    this.cdRef.detectChanges();
  }

  resetTextarea() {
    // Enable smooth transition BEFORE changing padding so the dismiss animates.
    // All other padding changes (footer toggle, blur) stay instant via the base transition:none.
    this.keyboardDismissing = true;
    this.keyboardShowing = false;
    this.keyboardOffset = 0;
    this.cdRef.detectChanges();
    setTimeout(() => {
      this.keyboardDismissing = false;
      this.cdRef.detectChanges();
    }, 300);
  }

  ngOnDestroy() {
    if (this.activeStreamInterval !== null) {
      clearInterval(this.activeStreamInterval);
      this.activeStreamInterval = null;
    }
    this.chatMutationObserver?.disconnect();
    this.httpCancel$.next();
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
